export type CalculatorMode = 'simple' | 'business' | 'units';

export interface CalculatorHistoryEntry {
  id: string;
  timestamp: string;
  expression: string;
  result: string;
  mode: CalculatorMode;
}

export interface CalculatorPrefs {
  version: 1;
  mode: CalculatorMode;
  memory: number | null;
  history: CalculatorHistoryEntry[];
  historyOpenDesktop: boolean;
}

export const CALCULATOR_HISTORY_LIMIT = 100;

const DEFAULTS: CalculatorPrefs = {
  version: 1,
  mode: 'simple',
  memory: null,
  history: [],
  historyOpenDesktop: true
};

function isMode(v: unknown): v is CalculatorMode {
  return v === 'simple' || v === 'business' || v === 'units';
}

function isHistoryEntry(v: unknown): v is CalculatorHistoryEntry {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.timestamp === 'string' &&
    typeof o.expression === 'string' &&
    typeof o.result === 'string' &&
    isMode(o.mode)
  );
}

export function trimHistory(entries: CalculatorHistoryEntry[]): CalculatorHistoryEntry[] {
  if (entries.length <= CALCULATOR_HISTORY_LIMIT) return entries;
  return entries.slice(0, CALCULATOR_HISTORY_LIMIT);
}

/** Each site passes its own key so calculator state never leaks between portals. */
export function loadCalculatorPrefs(storageKey: string): CalculatorPrefs {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { ...DEFAULTS, history: [] };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const historyRaw = Array.isArray(parsed.history) ? parsed.history.filter(isHistoryEntry) : [];
    const memory =
      typeof parsed.memory === 'number' && Number.isFinite(parsed.memory) ? parsed.memory : null;
    return {
      version: 1,
      mode: isMode(parsed.mode) ? parsed.mode : DEFAULTS.mode,
      memory,
      history: trimHistory(historyRaw),
      historyOpenDesktop:
        typeof parsed.historyOpenDesktop === 'boolean'
          ? parsed.historyOpenDesktop
          : DEFAULTS.historyOpenDesktop
    };
  } catch {
    return { ...DEFAULTS, history: [] };
  }
}

export function saveCalculatorPrefs(storageKey: string, prefs: CalculatorPrefs): void {
  try {
    const payload: CalculatorPrefs = {
      version: 1,
      mode: prefs.mode,
      memory: prefs.memory,
      history: trimHistory(prefs.history),
      historyOpenDesktop: prefs.historyOpenDesktop
    };
    localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function createHistoryEntry(
  expression: string,
  result: string,
  mode: CalculatorMode
): CalculatorHistoryEntry {
  return {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    expression,
    result,
    mode
  };
}
